import { EventType } from "@/analytics/analytics-events";
import { useElementFileUploadContext } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { useDataManagementContext } from "@/components/ui/data-management/data-management-context";
import { changeMode } from "@/store/mode-slice";
import {
  resetImportSheetAreaId,
  setImportSheetAreaId,
} from "@/store/modes/import-sheet-mode-slice";
import { selectActiveArea } from "@/store/selections-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { useImportAnnotations } from "@/utils/import-annotations-hook";
import { DraftViewOpenFrom, useNavigateToDraftView } from "@/utils/redirects";
import { useImport2DPhotos } from "@/utils/use-import-2d-photos";
import {
  FileCheckResult,
  FileTypesByCategory,
} from "@faro-lotv/data-management";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useEffect, useState } from "react";

export interface DataManagementFileHandlerProps {
  /** Project ID. */
  projectId?: string;
  /** Optional callback for closing the import data dialog. */
  onClose?(): void;
  /** Identifier for the calling UI page/element provided to navigateToDraftView. */
  from: DraftViewOpenFrom;
  /** Setter provided by the empty project page. If set to true, the sheet import page is shown. */
  setHasSelectedSheet?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface DataManagementFileHandlerReturnType {
  /**
   * Flag to signal the Data Management picker that it should open the browser's file
   * explorer.
   */
  shouldOpenFileExplorer: boolean;
  /**
   * Callback for setting the flag to signal the Data Management picker that it should open the
   * browser's file explorer.
   */
  setShouldOpenFileExplorer: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Flag whether the Data Management picker/dropzone is currently carrying out the file
   * verification.
   */
  isCheckingFiles: boolean;
  /**
   * Callback for setting whether the Data Management picker/dropzone is currently carrying out
   * the file verification.
   */
  setIsCheckingFiles: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Callback when the Data Management picker/dropzone has finished the file verification.
   */
  onFinishedCheckingFiles(result: FileCheckResult): void;
  /**
   * Mapping of file extensions by data type category as needed by the Data Management dropzone.
   * For the data type categories "Scan" and "ImageScan", the Data Management library handles the
   * extensions internally.
   * For data types the user is not allowed to import, the values stay null.
   */
  fileTypesByCategory: FileTypesByCategory;
}

/**
 * @returns Flags and functions to manage the state and behavior of the Data Management picker.
 * Apart from shouldOpenFileExplorer and setShouldOpenFileExplorer, they're also used by the
 * Data Management dropzone to handle drag & drop of files in the import data dialog.
 */
export function useDataManagementFileHandler({
  projectId,
  onClose,
  from,
  setHasSelectedSheet,
}: DataManagementFileHandlerProps): DataManagementFileHandlerReturnType {
  const dispatch = useAppDispatch();
  const activeArea = useAppSelector(selectActiveArea);
  const navigateToDraftView = useNavigateToDraftView();
  const { setPointCloudFile, setGaussianSplatsFile, setCadFile } =
    useElementFileUploadContext();
  const importAnnotations = useImportAnnotations();
  const import2DPhotos = useImport2DPhotos();
  const { setSheetFile, setScanFileInfos } = useDataManagementContext();

  const [shouldOpenFileExplorer, setShouldOpenFileExplorer] = useState(false);
  const [isCheckingFiles, setIsCheckingFiles] = useState<boolean>(false);

  const fileTypesByCategory: FileTypesByCategory = {
    PointCloud: null,
    GaussianSplat: null,
    CadModel: null,
    Area: null,
    Sheet: null,
    Annotation: null,
    Photo: null,
  };

  function onFinishedCheckingFiles(result: FileCheckResult | null): void {
    dispatch(setShowSpinner(false));

    if (result?.fileInfos?.length) {
      // File infos for the scan upload handled by the Data Management editor.
      setScanFileInfos(result.fileInfos);
      if (projectId) {
        navigateToDraftView(projectId, from);
      }

      if (onClose) {
        onClose();
      }
    } else if (result?.files?.length) {
      // File(s) handled by one of the other import mechanisms.
      const file = result.files[0];

      switch (result.selectedCategory) {
        case "PointCloud":
          setPointCloudFile(file);
          break;
        case "GaussianSplat":
          setGaussianSplatsFile(file);
          break;
        case "CadModel":
          setCadFile(file);
          break;
        case "Area":
          Analytics.track(EventType.startImportAreaWorkflow);
          setSheetFile(file);
          dispatch(resetImportSheetAreaId());
          dispatch(changeMode("importSheet"));
          break;
        case "Sheet":
          if (setHasSelectedSheet) {
            // Sheet import started from the empty project page.
            Analytics.track(EventType.startImportSheetWorkflow);
            setSheetFile(file);
            setHasSelectedSheet(true);
          } else if (activeArea) {
            // Sheet import started from the import data dialog.
            Analytics.track(EventType.startImportSheetWorkflow);
            setSheetFile(file);
            dispatch(setImportSheetAreaId(activeArea.id));
            dispatch(changeMode("importSheet"));
          }
          break;
        case "Photo":
          void import2DPhotos(result.files);
          break;
        case "Annotation":
          void importAnnotations(file);
          break;
        default:
          // Should not happen, as the picker only allows selecting files of known categories and
          // the "Scan" category is handled in the first if-branch.
          break;
      }

      if (onClose) {
        onClose();
      }
    }
  }

  // Show a spinner while checking the files.
  useEffect(() => {
    dispatch(setShowSpinner(isCheckingFiles));
  }, [dispatch, isCheckingFiles]);

  return {
    shouldOpenFileExplorer,
    setShouldOpenFileExplorer,
    isCheckingFiles,
    setIsCheckingFiles,
    onFinishedCheckingFiles,
    fileTypesByCategory,
  };
}
